import {
  ERROR_MESSAGES,
  generateSftpGroupPath,
  SFTP_NOT_FOR_INTERNAL_MESSAGE,
  STEERFI_PARTY_ID,
} from '@steerfi/shared';
import { IconLoader2, IconUserPlus } from '@tabler/icons-react';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type ClientData, getClients } from '@/api/clients';
import { type CreateUserRequest, createUser, type RealmRole } from '@/api/users';
import { getSftpFieldErrors, SftpAccessFields } from '@/components/sftp-access-fields';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  FormControl,
  type FormErrorItem,
  FormErrorSummary,
  FormField,
  focusFirstInvalid,
} from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { accessLevelFromScopeTier, rolesForAccessLevel } from '@/lib/authz';
import { formatErrorMessage } from '@/utils/error-formatting';

interface CreateUserModalProps {
  onSuccess?: () => void;
}

type FieldErrorKey =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'password'
  | 'partyId'
  | 'sftp'
  | 'sftpUsername'
  | 'sshPublicKeys';

type FieldErrors = Partial<Record<FieldErrorKey, string>> & {
  /** Which saved key `sshPublicKeys` is about, from `getSftpFieldErrors`. */
  invalidKeyIndex?: number;
};

const FIELD_LABELS: Record<FieldErrorKey, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  password: 'Password',
  partyId: 'Client',
  sftp: 'SFTP Access',
  sftpUsername: 'SFTP Username',
  sshPublicKeys: 'SSH Public Keys',
};

const FIELD_ORDER: FieldErrorKey[] = [
  'firstName',
  'lastName',
  'email',
  'password',
  'partyId',
  'sftp',
  'sftpUsername',
  'sshPublicKeys',
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMAIL_FORMAT_MESSAGE = 'Enter an email like name@company.com';

const initialFormData: CreateUserRequest = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  password: '',
  partyId: '',
  roles: ['basic'],
  isSftp: false,
  sftpUsername: '',
  sshPublicKeys: [],
};

function validateCreateUserForm(
  formData: CreateUserRequest,
  isInternalScope: boolean,
  sftpGroupPath: string
): FieldErrors {
  const errors: FieldErrors = {};

  if (!formData.firstName.trim()) {
    errors.firstName = 'Enter a first name';
  }
  if (!formData.lastName.trim()) {
    errors.lastName = 'Enter a last name';
  }
  if (!EMAIL_PATTERN.test(formData.email.trim())) {
    errors.email = EMAIL_FORMAT_MESSAGE;
  }
  if (!formData.password || formData.password.length < 8) {
    errors.password = 'Enter a password with at least 8 characters';
  }
  if (!isInternalScope && !formData.partyId) {
    errors.partyId = 'Select a client from the list';
  }

  const enableSftp = formData.isSftp === true;
  if (enableSftp) {
    if (isInternalScope) {
      errors.sftp = SFTP_NOT_FOR_INTERNAL_MESSAGE;
    } else if (!sftpGroupPath) {
      errors.sftp = 'Select a client to generate the SFTP group path';
    } else {
      Object.assign(
        errors,
        getSftpFieldErrors({
          sftpUsername: formData.sftpUsername,
          sshPublicKeys: formData.sshPublicKeys ?? [],
        })
      );
    }
  }

  return errors;
}

function toSummaryItems(fieldErrors: FieldErrors, formError: string | null): FormErrorItem[] {
  const items: FormErrorItem[] = FIELD_ORDER.filter((key) => fieldErrors[key]).map((key) => ({
    label: FIELD_LABELS[key],
    message: fieldErrors[key] as string,
  }));
  if (formError) {
    items.push({ label: 'Form', message: formError });
  }
  return items;
}

export const CreateUserModal: FC<CreateUserModalProps> = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const [formData, setFormData] = useState<CreateUserRequest>(initialFormData);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  // Access level is chosen as two orthogonal controls — scope (internal vs external)
  // and tier (basic vs admin) — translated to realm roles on submit.
  const [isInternalScope, setIsInternalScope] = useState(false);
  const [tier, setTier] = useState<'basic' | 'admin'>('basic');
  const formRef = useRef<HTMLFormElement>(null);

  const loadClients = useCallback(async () => {
    try {
      setClientsLoading(true);
      const response = await getClients({ limit: 100, sortBy: 'name', order: 'asc' });
      setClients(response.data);
    } catch {
      // Silently fail - user will see empty dropdown
    } finally {
      setClientsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadClients();
    }
  }, [open, loadClients]);

  const updateFormData = (
    key: keyof CreateUserRequest,
    value: string | boolean | string[] | RealmRole[]
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (key === 'firstName') delete next.firstName;
      if (key === 'lastName') delete next.lastName;
      if (key === 'email') delete next.email;
      if (key === 'password') delete next.password;
      if (key === 'partyId') {
        delete next.partyId;
        delete next.sftp;
      }
      if (key === 'isSftp') {
        delete next.sftp;
        delete next.sftpUsername;
        delete next.sshPublicKeys;
        delete next.invalidKeyIndex;
      }
      if (key === 'sftpUsername') delete next.sftpUsername;
      if (key === 'sshPublicKeys') {
        delete next.sshPublicKeys;
        delete next.invalidKeyIndex;
      }
      return next;
    });
  };

  // SFTP access is scoped to a client, since the Keycloak group path is derived
  // from the client name. With no client the path is blank, which the SFTP fields
  // surface and submit rejects.
  //
  // Derived from the *effective* party: internal users are assigned to SteerFi
  // regardless of any client picked earlier, so switching to internal scope must
  // drop the group rather than leave a stale client path behind.
  const effectivePartyId = isInternalScope ? '' : formData.partyId;
  const selectedClient = clients.find((c) => c.id === effectivePartyId);
  const sftpGroupPath = selectedClient ? generateSftpGroupPath(selectedClient.name) : '';

  const resetForm = () => {
    setFormData(initialFormData);
    setIsInternalScope(false);
    setTier('basic');
    setFieldErrors({});
    setFormError(null);
    setSubmitAttempt(0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError(null);

    const errors = validateCreateUserForm(formData, isInternalScope, sftpGroupPath);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitAttempt((n) => n + 1);
      setIsLoading(false);
      return;
    }

    setFieldErrors({});

    try {
      const enableSftp = formData.isSftp === true;
      const sshPublicKeys = formData.sshPublicKeys ?? [];

      const roles = rolesForAccessLevel(
        accessLevelFromScopeTier(isInternalScope, tier === 'admin')
      ) as RealmRole[];
      // Internal users belong to the SteerFi internal party, not a specific client.
      const partyId = isInternalScope ? STEERFI_PARTY_ID : formData.partyId;
      const trimmedPhone = formData.phoneNumber?.trim();
      const payload: CreateUserRequest = {
        ...formData,
        partyId,
        roles,
        ...(trimmedPhone ? { phoneNumber: trimmedPhone } : {}),
        // Only send SFTP fields when it is actually being enabled, so a
        // non-SFTP user is not rejected for carrying keys or a group.
        ...(enableSftp
          ? {
              isSftp: true,
              keycloakGroup: sftpGroupPath,
              sftpUsername: (formData.sftpUsername ?? '').trim() || null,
              sshPublicKeys,
            }
          : { isSftp: false, sftpUsername: null, sshPublicKeys: [] }),
      };

      await createUser(payload);

      setOpen(false);
      resetForm();
      onSuccess?.();
    } catch (err) {
      const errorMessage = formatErrorMessage(err);
      // Matched by exact text. A taken SFTP username is a different 409
      // sentence from Keycloak and stays on the form summary.
      if (errorMessage === ERROR_MESSAGES.USER_EMAIL_CONFLICT) {
        setFieldErrors({ email: errorMessage });
      } else {
        setFormError(errorMessage);
      }
      setSubmitAttempt((n) => n + 1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    setOpen(isOpen);
  };

  const summaryItems = toSummaryItems(fieldErrors, formError);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="px-3">
          <IconUserPlus className="mr-2 h-4 w-4" />
          New User
        </Button>
      </DialogTrigger>
      {/* Capped height with the body as the only scroll region, so the header,
          the error banner, and the footer buttons stay reachable on short
          viewports — the form is tall once SFTP access is expanded. */}
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] !flex !flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            Create a new user and assign them to a client with specific permissions. Fields marked
            with * are required.
          </DialogDescription>
        </DialogHeader>
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 min-h-0"
          autoComplete="off"
          noValidate
        >
          <FormErrorSummary
            errors={summaryItems}
            attempt={submitAttempt}
            onAnnounced={() => focusFirstInvalid(formRef.current)}
          />
          <div className="grid gap-4 py-4 flex-1 min-h-0 overflow-y-auto">
            <FormField label="First Name" orientation="row" required error={fieldErrors.firstName}>
              <FormControl>
                <Input
                  value={formData.firstName}
                  onChange={(e) => updateFormData('firstName', e.target.value)}
                  placeholder="Enter first name"
                />
              </FormControl>
            </FormField>

            <FormField label="Last Name" orientation="row" required error={fieldErrors.lastName}>
              <FormControl>
                <Input
                  value={formData.lastName}
                  onChange={(e) => updateFormData('lastName', e.target.value)}
                  placeholder="Enter last name"
                />
              </FormControl>
            </FormField>

            <FormField label="Email" orientation="row" required error={fieldErrors.email}>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="off"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  placeholder="user@example.com"
                />
              </FormControl>
            </FormField>

            <FormField label="Phone" orientation="row">
              <FormControl>
                <Input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => updateFormData('phoneNumber', e.target.value)}
                  placeholder="Optional"
                />
              </FormControl>
            </FormField>

            <FormField label="Password" orientation="row" required error={fieldErrors.password}>
              <FormControl>
                <Input
                  type="password"
                  // new-password, not off. Browsers ignore off on a password
                  // next to an email and insert the saved login. This is an
                  // account being created for someone else.
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  placeholder="Minimum 8 characters"
                  minLength={8}
                />
              </FormControl>
            </FormField>

            <FormField
              label="Client"
              orientation="row"
              required={!isInternalScope}
              error={fieldErrors.partyId}
            >
              <Select
                value={isInternalScope ? '' : formData.partyId}
                onValueChange={(value) => updateFormData('partyId', value)}
                disabled={isInternalScope}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        isInternalScope
                          ? 'SteerFi (internal user)'
                          : clientsLoading
                            ? 'Loading clients...'
                            : 'Select a client'
                      }
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <div className={`grid grid-cols-4 gap-4 items-start`}>
              <span className="text-sm font-medium text-right pt-2">Scope</span>
              <div className="col-span-3 flex items-center space-x-2">
                <Checkbox
                  id="scope-internal"
                  checked={isInternalScope}
                  onCheckedChange={(checked) => {
                    setIsInternalScope(checked === true);
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.partyId;
                      delete next.sftp;
                      return next;
                    });
                  }}
                  disabled={isLoading}
                />
                <Label htmlFor="scope-internal" className="font-normal cursor-pointer">
                  <span className="font-medium">SteerFi internal user</span>
                  <span className="text-muted-foreground ml-1">— cross-client internal access</span>
                </Label>
              </div>
            </div>

            <FormField label="Role" orientation="row" required>
              <Select value={tier} onValueChange={(value) => setTier(value as 'basic' | 'admin')}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            <div className="border-t pt-4 mt-2">
              <div className="grid grid-cols-4 gap-4 items-start">
                <span className="text-sm font-medium text-right pt-2">SFTP Access</span>
                <div className="col-span-3 space-y-1.5">
                  <SftpAccessFields
                    idPrefix="create"
                    isSftp={formData.isSftp === true}
                    onIsSftpChange={(value) => updateFormData('isSftp', value)}
                    sftpUsername={formData.sftpUsername ?? ''}
                    onSftpUsernameChange={(value) => updateFormData('sftpUsername', value)}
                    sshPublicKeys={formData.sshPublicKeys ?? []}
                    onSshPublicKeysChange={(keys) => updateFormData('sshPublicKeys', keys)}
                    email={formData.email}
                    groupPath={sftpGroupPath}
                    enableNotice="SFTP users are created without MFA or email verification. This user will not be able to log into the web app without enrolling in MFA."
                    disabled={isLoading}
                    ineligibleReason={isInternalScope ? SFTP_NOT_FOR_INTERNAL_MESSAGE : null}
                    toggleError={fieldErrors.sftp}
                    usernameError={fieldErrors.sftpUsername}
                    keysError={fieldErrors.sshPublicKeys}
                    invalidKeyIndex={fieldErrors.invalidKeyIndex}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
